import {ReportableError} from '@wix/document-manager-utils'
import type {DalValue, Pointer} from '@wix/document-services-types'
import {createImmutableProxy} from '@wix/wix-immutable-proxy'
import _ from 'lodash'
import type {Null} from '../../types'
import {deepCompare as _deepCompare} from '../../utils/deepCompare'
import {createStore, DalJsStore, DmStore} from '../store'
import type {SnapshotList} from './snapshotList'
import type {FragmentDalJsStore, FragmentStore} from '../fragment/fragmentStore'
import {PAGE_FRAGMENT_TYPE} from '../documentManagerDal'
import {deepCompareIgnoreSignature} from '../../utils/deepCompareIgnoreSignature'

/**
 * Returns a list of all the snapshots from but not including fromSnapshot, up to and including toSnapshot
 * @param {SnapshotDal} fromSnapshot
 * @param {SnapshotDal} toSnapshot
 * @param source
 * @returns {SnapshotDal[]}
 */
function createSnapshotChain(
    fromSnapshot: Null<SnapshotDal>,
    toSnapshot: Null<SnapshotDal>,
    source: string
): SnapshotDal[] {
    const chain: SnapshotDal[] = []
    let snapshot: Null<SnapshotDal> = toSnapshot
    let containsSnapshotId = false
    while (snapshot !== null && snapshot.id !== fromSnapshot?.id) {
        containsSnapshotId = containsSnapshotId || fromSnapshot?.id === snapshot.id
        chain.push(snapshot)
        snapshot = snapshot.getPreviousSnapshot()
    }
    if (snapshot === null && snapshot !== fromSnapshot) {
        throw new ReportableError({
            message: 'Could not locate the source snapshot',
            errorType: 'failedToCreateSnapshotChain',
            extras: {source, fromSnapshot: fromSnapshot?.id, toSnapshot: toSnapshot?.id, containsSnapshotId}
        })
    }
    return chain.reverse()
}

// A private method that returns the value for the given pointer at the time when the snapshot was taken
const getRawValue = (snapshot: Null<SnapshotDal>, pointer: Pointer): any => {
    let tempSnap: Null<SnapshotDal> = snapshot
    while (tempSnap) {
        if (tempSnap._snapshotStore.has(pointer)) {
            return tempSnap._snapshotStore.get(pointer)
        }
        tempSnap = tempSnap.getPreviousSnapshot()
    }
    return undefined
}

// A private method that returns the difference between this snapshot and the from snapshot
const getRawDiff = (thisSnapshot: SnapshotDal, fromSnapshot: Null<SnapshotDal>, fragmentType?: string): DalJsStore => {
    // Iterating forwards and applying each snapshot in succession allows easy handling of deleted pointers
    const chain = createSnapshotChain(fromSnapshot, thisSnapshot, 'getRawDiff')
    const js: DalJsStore = {}
    chain.forEach(snapshot => {
        if (fragmentType) {
            snapshot.getAllFragmentsStore().getFragmentStore(fragmentType)?.applyValues(js)
        } else {
            snapshot.getStore().applyValues(js)
        }
    })
    return js
}

// get raw diff for the entire store- as a map of fragment type to diff in this fragment store
const getFragmentsRawDiff = (thisSnapshot: SnapshotDal, fromSnapshot: Null<SnapshotDal>): FragmentDalJsStore => {
    const chain = createSnapshotChain(fromSnapshot, thisSnapshot, 'getFragmentsRawDiff')
    const fragmentsSnapshotStore: FragmentDalJsStore = {}
    chain.forEach(snapshot => {
        snapshot._snapshotStore.forEach((_fragmentStore, fragmentType) => {
            const js: DalJsStore = fragmentsSnapshotStore[fragmentType] ?? {}
            snapshot.getFragmentStore(fragmentType)?.applyValues(js)
            fragmentsSnapshotStore[fragmentType] = js
        })
    })
    return fragmentsSnapshotStore
}

type CompareCB = (isEqual: boolean, type: string, id: string, originalValue: any, fragmentType?: string) => void
type DeepCompare = (a: DalValue, b: DalValue) => boolean

const compareEntries = (
    fromSnapshot: Null<SnapshotDal>,
    changedValues: any,
    type: string,
    callback: CompareCB,
    fragmentType?: string,
    deepCompare: DeepCompare = _deepCompare
) => {
    Object.entries(changedValues).forEach(([id, changedValue]) => {
        const originalValue = getRawValue(fromSnapshot, {id, type, fragmentType})
        const isEqual = deepCompare(originalValue, changedValue)
        callback(isEqual, type, id, originalValue, fragmentType)
    })
}

const compareValuesToOriginal = (
    diff: DalJsStore | FragmentDalJsStore,
    isMultiFragmentsDiff: boolean,
    fromSnapshot: Null<SnapshotDal>,
    callback: CompareCB,
    deepCompare?: DeepCompare
): void => {
    if (isMultiFragmentsDiff) {
        Object.entries(diff).forEach(([fragmentType, fragmentDiff]) => {
            Object.entries(fragmentDiff as any).forEach(([type, changedValues]) => {
                compareEntries(fromSnapshot, changedValues, type, callback, fragmentType, deepCompare)
            })
        })
    } else {
        Object.entries(diff).forEach(([type, changedValues]) => {
            compareEntries(fromSnapshot, changedValues, type, callback, undefined, deepCompare)
        })
    }
}

/**
 * A SnapshotDal object reflects the data in the Document Manager Dal at the time the SnapshotDal was created.
 */
class SnapshotDal {
    _isForeign: boolean = false
    _isUndoable: boolean = false
    _wasRevertedByCheckpoint: boolean = false

    public lastTransactionId?: string

    constructor(
        public _previousSnapshot: Null<SnapshotDal>,
        public readonly _snapshotStore: FragmentStore,
        public readonly id: string = ''
    ) {}

    getPreviousSnapshot(): Null<SnapshotDal> {
        return this._previousSnapshot
    }

    /** Returns true if the pointer exists at the time when the snapshot was taken */
    exists(pointer: Pointer) {
        return getRawValue(this, pointer) !== undefined
    }

    /** Returns true if the snapshot contains the pointer in it's store */
    contains(pointer: Pointer) {
        return this._snapshotStore.has(pointer)
    }

    /** Returns the immutable value for the given pointer at the time when the snapshot was taken
     * The pointer parameter can have 3 properties:
     * type (string), id (string), and innerPath (string or array of strings)
     */
    getValue(pointer: Pointer) {
        let value = getRawValue(this, pointer)
        if (typeof value !== 'object' || value === null) {
            if (pointer.innerPath?.length) {
                return undefined
            }
            return value
        }
        let {innerPath} = pointer
        if (innerPath) {
            if (typeof innerPath === 'string') {
                innerPath = [innerPath]
            }
            if (innerPath.length) {
                value = _.get(value, innerPath)
            }
        }
        return createImmutableProxy(value)
    }

    /** Creates an object that when applied will revert from toSnapshot back to fromSnapshot */
    createRevertJS(fromSnapshot: Null<SnapshotDal>) {
        const diff: DalJsStore = getRawDiff(this, fromSnapshot)
        const result = {}
        compareValuesToOriginal(diff, false, fromSnapshot, (valueIsEqual, type, id, originalValue) => {
            // Only add the value if it's really different
            if (!valueIsEqual) {
                let typeStore = result[type]
                if (!typeStore) {
                    typeStore = {}
                    result[type] = typeStore
                }
                typeStore[id] = originalValue
            }
        })
        return createImmutableProxy(result)
    }

    /** This returns a diff that is not wrapped in an immutable proxy.
     * THE VALUES IN THE DIFF ARE REFERENCES TO DATA THAT MUST NOT BE MODIFIED.
     * Please make sure that the pointer values returned by this function are not be modified.
     */
    mutableDiff(fromSnapshot: SnapshotDal | null, fragmentType?: string, deepCompare?: DeepCompare): DalJsStore {
        if (!fromSnapshot) {
            return this.toJS(fragmentType)
        }
        const diff: DalJsStore = getRawDiff(this, fromSnapshot, fragmentType)
        compareValuesToOriginal(
            diff,
            false,
            fromSnapshot,
            (isEqual, type, id) => {
                if (isEqual) {
                    delete diff[type][id]
                }
            },
            deepCompare
        )
        return diff
    }

    /** This returns a diff that is not wrapped in an immutable proxy.
     * The diff refers to the entire store (all fragments in it) and not only for one fragment.
     * this will replace the current mutableDiff that will be deleted
     */
    fragmentMutableDiff(fromSnapshot: SnapshotDal | null, deepCompare?: DeepCompare): FragmentDalJsStore {
        if (!fromSnapshot) {
            return this.toJS() as FragmentDalJsStore
        }
        const diff: FragmentDalJsStore = getFragmentsRawDiff(this, fromSnapshot)
        compareValuesToOriginal(
            diff,
            true,
            fromSnapshot,
            (valueIsEqual, type, id, _original, fragmentType) => {
                if (!fragmentType) return
                if (valueIsEqual) {
                    delete diff[fragmentType][type][id]
                }
                if (Object.keys(diff[fragmentType][type]).length === 0) {
                    delete diff[fragmentType][type]
                }
                if (Object.keys(diff[fragmentType]).length === 0) {
                    delete diff[fragmentType]
                }
            },
            deepCompare
        )
        return diff
    }

    /** Returns an object representing the difference in state from this snapshot to fromSnapshot. wrapped in immutableProxy*/
    diff(fromSnapshot: SnapshotDal | null, fragmentType?: string): DalJsStore | FragmentDalJsStore {
        const mutableDiff = fragmentType
            ? this.mutableDiff(fromSnapshot, fragmentType)
            : this.fragmentMutableDiff(fromSnapshot)
        return createImmutableProxy(mutableDiff)
    }

    /** Returns an object representing the difference in state from this snapshot to fromSnapshot. wrapped in immutableProxy*/
    diffDeepCompare(fromSnapshot: SnapshotDal | null, fragmentType?: string): DalJsStore | FragmentDalJsStore {
        const mutableDiff = fragmentType
            ? this.mutableDiff(fromSnapshot, fragmentType, deepCompareIgnoreSignature)
            : this.fragmentMutableDiff(fromSnapshot, deepCompareIgnoreSignature)
        return createImmutableProxy(mutableDiff)
    }

    /** Returns an object containing all the values represented by this snapshot for the specified type. */
    toJSForType(type: string): any {
        const chain = createSnapshotChain(null, this, 'toJSForType')
        const js = {}
        chain.forEach(snapshot => {
            snapshot.getStore().applyTypeValuesWithDelete(type, js)
        })
        return createImmutableProxy(js)
    }

    /** Returns an object representing the state when this snapshot was taken */
    toJS(fragmentType?: string): DalJsStore | FragmentDalJsStore {
        const chain = createSnapshotChain(null, this, 'toJS')
        const js: DalJsStore | FragmentDalJsStore = {}
        chain.forEach(snapshot => {
            const store = fragmentType ? snapshot.getFragmentStore(fragmentType) : snapshot._snapshotStore
            store.applyValuesWithDelete(js)
        })
        return createImmutableProxy(js)
    }

    isPredecessorOf(snapshot: SnapshotDal) {
        let _snapshot: Null<SnapshotDal> = snapshot.getPreviousSnapshot()
        while (_snapshot) {
            if (_snapshot === this) {
                return true
            }
            _snapshot = _snapshot.getPreviousSnapshot()
        }
        return false
    }

    equals(otherSnapshot: SnapshotDal): boolean {
        return this === otherSnapshot
    }

    createSnapshotChainTo(toSnapshot: Null<SnapshotDal>) {
        return createSnapshotChain(this, toSnapshot, 'createSnapshotChainTo')
    }

    setPreviousSnapshot(otherSnapshot: Null<SnapshotDal>): void {
        this._previousSnapshot = otherSnapshot
    }

    get isForeign() {
        return this._isForeign
    }

    setAsForeign() {
        this._isForeign = true
    }

    get wasRevertedByCheckpoint() {
        return this._wasRevertedByCheckpoint
    }

    setAsRevertedByCheckpoint() {
        this._wasRevertedByCheckpoint = true
    }

    get isUndoable() {
        return this._isUndoable
    }

    setAsUndoable() {
        this._isUndoable = true
    }

    /**
     * find next snapshot
     * @param {SnapshotList} list
     * @returns {SnapshotDal}
     */
    findNext(list: SnapshotList): Null<SnapshotDal> {
        return list.find(s => s._previousSnapshot === this)
    }

    /**
     * Returns the internal store when not using fragments at all.
     * THE RETURNED STORE MUST NOT BE MODIFIED. It should only be used for reading values.
     * Store values can be copied into other stores because store values are never modified, only replaced.
     */
    getStore(): DmStore {
        return this.getFragmentStore(PAGE_FRAGMENT_TYPE)
    }

    /**
     * Returns the internal fragments store (all fragment types mapped to their store). used only when using fragments.
     * will eventually replace getStore()
     * THE RETURNED STORE MUST NOT BE MODIFIED. It should only be used for reading values.
     * Store values can be copied into other stores because store values are never modified, only replaced.
     */
    getAllFragmentsStore(): FragmentStore {
        return this._snapshotStore
    }

    getFragmentStore(fragmentType: string): DmStore {
        return this._snapshotStore.getFragmentStore(fragmentType) ?? createStore()
    }

    _toArray() {
        const arr: SnapshotDal[] = []
        // eslint-disable-next-line @typescript-eslint/no-this-alias,consistent-this
        let t: Null<SnapshotDal> = this
        while (t !== null) {
            arr.push(t)
            t = t.getPreviousSnapshot()
        }
        return arr.reverse()
    }

    [Symbol.iterator](): Iterator<Null<SnapshotDal>> {
        let curr: SnapshotDal | null
        // eslint-disable-next-line @typescript-eslint/no-this-alias
        const self = this
        return {
            next(): IteratorResult<Null<SnapshotDal>> {
                curr = curr ? curr._previousSnapshot : self
                return {
                    done: !curr,
                    value: curr
                }
            }
        }
    }
}

export {SnapshotDal, createSnapshotChain}
